// Module: typesetExportRenderer
// -----------------------------------------------------------------
// Receives:  typeset shapes (math, rich-text), the page rect of the layer
//            they are drawn into, and that layer's 2D context
// Produces:  the shapes drawn onto the context, from ONE SVG that carries
//            their live HTML and the page's CSS (→ typesetExportCss)
// Owns:      nothing between calls
// Does NOT:  decide which shapes come here or cache the result
//            (→ rasterize / typesetLayerCache)
// -----------------------------------------------------------------
//
// A shape this cannot draw faithfully is handed back for tldraw to draw:
// without live DOM, or holding media (an SVG image cannot fetch it).

import { Box, Mat, tlenv, type Editor, type TLShape } from "tldraw";
import type { TypesetRenderResult } from "./rasterize";
import {
  TYPESET_EXPORT_ANCESTOR_CLASS,
  buildTypesetExportCss,
  type TypesetFaceNeeds,
} from "./typesetExportCss";

const XHTML_NS = "http://www.w3.org/1999/xhtml";

// Safari reports the image loaded before it has painted a foreignObject; this
// is the wait tldraw's own getSvgAsImage uses for the same reason.
const SAFARI_FOREIGN_OBJECT_SETTLE_MS = 250;

// Media an SVG image cannot load, inline styles that may fetch.
const EXTERNAL_MEDIA_SELECTOR =
  "img, picture, video, audio, canvas, iframe, object, embed";

export interface TypesetExportSvgInput {
  css: string;
  /** Opening/closing tags of the ancestor clones, outermost first. */
  ancestor_open: string;
  ancestor_close: string;
  /** One positioned element per shape, in paint order. */
  pieces: string[];
  /** Page-space size of the layer. */
  width: number;
  height: number;
  /** Pixel size of the image. */
  output_w_px: number;
  output_h_px: number;
}

interface DrawableShape {
  shapeEl: HTMLElement;
  /** The shape's `.tl-html-container`, serialized once as XHTML. */
  content_xhtml: string;
}

/**
 * Input:   editor; shapes in paint order; the layer's page rect; its context
 * Output:  the shapes NOT drawn, for the caller to draw another way, and
 *          whether the drawn ones got every face they need
 * Mutates: ctx (the drawn shapes, scaled to the whole canvas)
 *
 * Never throws: on any failure every shape is handed back, so a screenshot
 * degrades to the tldraw path instead of losing its formulas.
 */
export async function renderTypesetWithPageCss(
  editor: Editor,
  shapes: TLShape[],
  bounds: Box,
  ctx: CanvasRenderingContext2D,
): Promise<TypesetRenderResult> {
  if (shapes.length === 0) return { handed_back: [], is_complete: true };
  try {
    const container = editor.getContainer();
    const leftover: TLShape[] = [];
    const pieces: string[] = [];
    let ancestorTags: [string, string] | null = null;
    for (const shape of shapes) {
      const drawable = _findDrawableShape(container, shape);
      if (!drawable) {
        leftover.push(shape);
        continue;
      }
      ancestorTags ??= _buildAncestorTags(drawable.shapeEl);
      pieces.push(_buildShapePiece(editor, drawable, shape, bounds));
    }
    if (pieces.length === 0 || !ancestorTags) {
      return { handed_back: leftover, is_complete: true };
    }

    const { css, is_complete } = await buildTypesetExportCss(
      container,
      _findFaceNeeds(ancestorTags[0], pieces),
    );
    const svg = buildTypesetExportSvg({
      css,
      ancestor_open: ancestorTags[0],
      ancestor_close: ancestorTags[1],
      pieces,
      width: bounds.w,
      height: bounds.h,
      output_w_px: ctx.canvas.width,
      output_h_px: ctx.canvas.height,
    });
    const image = await _decodeSvg(svg);
    ctx.drawImage(image, 0, 0, ctx.canvas.width, ctx.canvas.height);
    return { handed_back: leftover, is_complete };
  } catch (error) {
    console.warn(
      "[typesetExportRenderer] falling back to tldraw for the typeset layer",
      error,
    );
    return { handed_back: shapes, is_complete: true };
  }
}

/**
 * Input:   the stylesheet, ancestor clone tags, shape pieces, sizes
 * Output:  the SVG document as a string
 *
 * The CSS sits inside the foreignObject so the image applies it; `<` and `&`
 * are escaped because the SVG is parsed as XML.
 */
export function buildTypesetExportSvg(input: TypesetExportSvgInput): string {
  const { width, height } = input;
  const css = input.css.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${input.output_w_px}" height="${input.output_h_px}" viewBox="0 0 ${width} ${height}">` +
    `<foreignObject x="0" y="0" width="${width}" height="${height}">` +
    `<div xmlns="${XHTML_NS}" style="position:relative;width:${width}px;height:${height}px;overflow:hidden;">` +
    `<style>${css}</style>` +
    input.ancestor_open +
    input.pieces.join("") +
    input.ancestor_close +
    `</div></foreignObject></svg>`
  );
}

/**
 * The faces the markup asks for: bold and italic from rich-text marks, inline
 * styles and KaTeX's `\textbf`/`\textit`, and the classes and ids that
 * decide which KaTeX faces apply. A missed variant falls back to the regular
 * face, never to a missing glyph.
 */
function _findFaceNeeds(
  ancestorOpen: string,
  pieces: string[],
): TypesetFaceNeeds {
  const html = pieces.join("");
  const used_tokens = new Set<string>();
  for (const [, attr, value] of (ancestorOpen + html).matchAll(
    /\s(class|id)="([^"]*)"/g,
  )) {
    const prefix = attr === "class" ? "." : "#";
    for (const name of value.split(/\s+/)) {
      if (name) used_tokens.add(prefix + name);
    }
  }
  return {
    is_bold_used:
      /<(strong|b|h[1-6])[\s>]|font-weight:\s*(bold|[6-9]00)|\btextbf\b/i.test(
        html,
      ),
    is_italic_used: /<(em|i)[\s>]|font-style:\s*italic|\btextit\b/i.test(html),
    used_tokens,
  };
}

/**
 * The shape's `.tl-shape` element and its content, or null when this path
 * cannot reproduce it: no live DOM, or holding media the image could not
 * load. A shape scrolled out of view is still in the DOM, only not displayed.
 *
 * The DOM is never older than the records the caller keys its cache on. Shapes
 * subscribe through useSyncExternalStore, so a record change commits in a
 * microtask queued at the change, and the screenshot reads here only after an
 * await. A record changing mid-screenshot leaves the DOM newer than the key,
 * which the next screenshot sees as a change and redraws.
 */
function _findDrawableShape(
  container: HTMLElement,
  shape: TLShape,
): DrawableShape | null {
  const shapeEl = container.querySelector<HTMLElement>(
    `.tl-shape[data-shape-id="${shape.id}"]`,
  );
  const content = shapeEl?.querySelector<HTMLElement>(".tl-html-container");
  if (!shapeEl || !content) return null;
  if (content.querySelector(EXTERNAL_MEDIA_SELECTOR)) return null;
  // XMLSerializer emits XHTML (closed voids, the namespace), which a
  // foreignObject parsed as XML requires.
  const content_xhtml = new XMLSerializer().serializeToString(content);
  if (content_xhtml.includes("url(")) return null;
  return { shapeEl, content_xhtml };
}

/**
 * The shape's opacity from the records, as tldraw's export takes it. The live
 * wrapper's opacity also carries UI state — a shape under the eraser is drawn
 * at a third — which the cached layer must not keep.
 */
function _findShapeOpacity(editor: Editor, shape: TLShape): number {
  let opacity = shape.opacity;
  for (const ancestor of editor.getShapeAncestors(shape)) {
    opacity *= ancestor.opacity;
  }
  return opacity;
}

/**
 * The shape as positioned in the layer: its wrapper keeps the classes
 * (`.tl-shape .tl-rich-text` must match) and is sized and transformed as
 * tldraw sizes and transforms it on the page, moved to the layer's origin.
 */
function _buildShapePiece(
  editor: Editor,
  drawable: DrawableShape,
  shape: TLShape,
  layerBounds: Box,
): string {
  const transform = Mat.Compose(
    Mat.Translate(-layerBounds.x, -layerBounds.y),
    editor.getShapePageTransform(shape),
  );
  const { width, height } = editor.getShapeGeometry(shape).bounds;
  const style =
    `position:absolute;left:0;top:0;transform-origin:0 0;transform:${Mat.toCssString(transform)};` +
    `width:${Math.max(width, 1)}px;height:${Math.max(height, 1)}px;overflow:visible;` +
    `opacity:${_findShapeOpacity(editor, shape)};`;
  return `<div class="${_escapeAttr(drawable.shapeEl.className)}" data-shape-type="${_escapeAttr(shape.type)}" style="${style}">${drawable.content_xhtml}</div>`;
}

/**
 * Empty clones of the shape's ancestors below <html>: tag, id, classes and
 * inline custom properties (tldraw keeps --tl-scale and friends inline), so
 * selectors match as on the page while no ancestor geometry reaches the
 * export. The theme is pinned light, as tldraw pins its exports.
 */
function _buildAncestorTags(shapeEl: HTMLElement): [string, string] {
  const chain: HTMLElement[] = [];
  for (
    let el = shapeEl.parentElement;
    el && el !== document.documentElement;
    el = el.parentElement
  ) {
    chain.unshift(el);
  }
  let open = "";
  let close = "";
  for (const el of chain) {
    const tag = el === document.body ? "div" : el.tagName.toLowerCase();
    const id = el.id ? ` id="${_escapeAttr(el.id)}"` : "";
    const classes =
      `${typeof el.className === "string" ? el.className : ""} ${TYPESET_EXPORT_ANCESTOR_CLASS}`
        .replace(/\btl-theme__dark\b/, "tl-theme__light")
        .trim();
    const vars = Array.from(el.style)
      .filter((name) => name.startsWith("--"))
      .map((name) => `${name}:${el.style.getPropertyValue(name)};`)
      .join("");
    open += `<${tag}${id} class="${_escapeAttr(classes)}" style="${_escapeAttr(vars)}position:static;transform:none;width:auto;margin:0;padding:0;overflow:visible;clip-path:none;filter:none;opacity:1;visibility:visible;display:block;">`;
    close = `</${tag}>` + close;
  }
  return [open, close];
}

/**
 * A data: URL with crossOrigin, as tldraw's getSvgAsImage: Chrome taints the
 * canvas for a blob: URL SVG holding a foreignObject, and a tainted canvas
 * cannot be encoded.
 */
async function _decodeSvg(svg: string): Promise<HTMLImageElement> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("typeset export SVG did not load"));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
  if (tlenv.isSafari) {
    await new Promise((resolve) =>
      setTimeout(resolve, SAFARI_FOREIGN_OBJECT_SETTLE_MS),
    );
  }
  return image;
}

function _escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}
